import CountryISOCode from '../../utils/CountryISOCode.ts'
import {b2bMarketFlag, b2bMarketLabel} from '../../utils/b2bMarketDisplay.ts'

export type MarketOption = {
    value: string
    label: string
    flag: string
}

// Every ISO 3166-1 alpha-2 country, so an agency's primaryMarket can be set to any
// market rather than the original handful of curated ones. Labels/flags are resolved
// the same way as the b2b reporting market codes (see b2bMarketDisplay.ts) so both
// stay in sync — this list needs no manual upkeep as new markets come up.
export const getMarketOptions = (): MarketOption[] =>
    CountryISOCode.allCodes()
        .map((isoCode) => ({
            value: isoCode.alpha2Code,
            label: b2bMarketLabel(isoCode.alpha2Code),
            flag: b2bMarketFlag(isoCode.alpha2Code) ?? ''
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es'))

// primaryMarket values persisted before the switch to ISO 3166-1 alpha-2 codes —
// kept so agencies saved under the old scheme still resolve/display correctly.
const LEGACY_MARKET_ISO_ALIASES: Record<string, string> = {
    spain: 'ES',
    usa: 'US',
    canada: 'CA',
    france: 'FR',
    italy: 'IT',
    australia: 'AU'
}

export const normalizeMarketValue = (value?: string): string | undefined =>
    value ? (LEGACY_MARKET_ISO_ALIASES[value] ?? value) : value

// The raw agency record is sometimes keyed `market`, sometimes `primaryMarket`
// depending on which layer produced it — read both defensively, normalizing legacy
// English-word primaryMarket values to their ISO equivalent so they resolve the same
// as current ISO-coded markets.
export const resolveAgencyMarket = (agency: {market?: string; primaryMarket?: string}): MarketOption | null => {
    const value = normalizeMarketValue(agency.market ?? agency.primaryMarket)
    if (!value) return null
    const marketByValue = Object.fromEntries(getMarketOptions().map((option) => [option.value, option]))
    return marketByValue[value] ?? {value, label: b2bMarketLabel(value), flag: b2bMarketFlag(value) ?? ''}
}
